import {
  defineStore,
  storeToRefs,
} from 'pinia';
import api from '@/services/api';
import work_order_model from '../models/work_order_model';
import work_order_filters_model from '../models/work_order_filters_model';
import work_order_producto_model from '../models/work_order_producto_model';

import initial_delivery_model from '@/shared/models/delivery_model.js';
import { getParamsUrl } from '../work_order_utils';
import { useAuthStore } from '../../auth/auth_store';
import elemento_grupo_service from '@/services/elemento_grupo_service';
import { Modules } from '@/enum/modules';
import { generarElemento } from '@/shared/generators/genericGenerator';
import { ElementoBase } from '@/enum/elemento_base';
import { Proceso } from '@/enum/proceso';
import { useElementoBaseStore } from '@/stores/elemento_base/elemento_base_store';
import { useConfiguracionStore } from '@/stores/configuracion-general.js';
import { Role } from '@/enum/roles_enum.js';
import { cantidadesIguales } from '@/shared/utils/cantidad_unidad_venta.js';

export const useWorkOrderStore = defineStore('workOrderStore', {
  state: () => ({
    ordenProduccion: JSON.parse(JSON.stringify(work_order_model)),
    filters: { ...work_order_filters_model },
    validarOrdenProduccionCampos: {
      Referencia: true,
      CodigoCliente: true,
      ClienteDescripcion: true,
      CodigoUnidadVenta: true,
      SolicitudCantidad: [],
      MedidaEje: true,
      MedidaDesarrollo: true,
      CantidadEtiquetas: true,
      FamiliaMaterial: true,
      Forma: true,
      GapEje: true,
      GapDesarrollo: true,
      EtiquetasDesarrollo: true,
      Sellado: true,
      Solapa: true,
      Base: true,
      AltoTotal: true,
      AnchoMaterial: true,
    },
    // Nombres de los campos que fallaron en la última validación, para poder decirle al
    // usuario qué falta en vez de un "revisar formulario" a secas.
    camposOrdenProduccionInvalidos: [],
    ordenesProduccion: [],
    page: 1,
    modalPedidosVisible: false,
    modalPedidosAOpVisible: false,
  }),
  getters: {
    getEtAsignadasFromRoute: (state) => (route) => {
      if (route?.name !== 'workOrderEdit') return [];

      const productos = state.ordenProduccion?.OrdenProduccionProducto;
      if (!Array.isArray(productos) || productos.length === 0) return [];
      return productos
        .map((productoOP) => productoOP?.CodigoEspecificacionTecnica)
        .filter((codigo) => Number(codigo) > 0);
    },
    cantidadesValidadas: (state) => {
      const headerQty = Number(state.ordenProduccion?.OrdenProduccionCantidad?.[0]?.Cantidad || 0);
      const productos = state.ordenProduccion?.OrdenProduccionProducto || [];

      if (productos.length > 0) {
        const sumDetalle = productos.reduce((acc, p) => acc + Number(p.Cantidad || 0), 0);
        if (!cantidadesIguales(sumDetalle, headerQty)) return false;
      }

      return true;
    },
  },

  actions: {
    async findAllOrdenesProduccionHTTP(params) {
      const authStore = useAuthStore();
      const usuario = authStore.usuario();
      const esEjecutivoDeVentas = usuario.roles.some(
        (rol) => rol.Codigo === Role.EJECUTIVO_DE_VENTAS,
      );

      if (esEjecutivoDeVentas) {
        this.filters.CodigoUsuarioCreacion = usuario.Codigo;
      }

      const result = await api.get(
        `/orden-produccion?${getParamsUrl({ ...this.filters, page: +this.page, ...params })}`,
      );
      if (result && result.data && result.data.length > 0) {
        this.ordenesProduccion.push(...result.data);
        this.setPage(this.page + 1);
        return true;
      }
      return false;
    },
    async firstRequest() {
      this.setPage(1);
      this.emptyList();
      this.resetFilter();
      await this.findAllOrdenesProduccionHTTP();
    },
    async createOrdenProduccionHTTP() {
      const response = await api.post('/orden-produccion', this.ordenProduccion);
      if (response.status === 200 || response.status === 201) {
        return response;
      }
    },
    async createFromETHTTP(codigoET, payload) {
      const res = await api.post(
        `/orden-produccion/from-especificacion-tecnica/${codigoET}`,
        payload,
      );
      if (res.status === 200 || res.status === 201) return res.data;
      return null;
    },
    async createFromPedidoHTTP(payload) {
      const res = await api.post(`/orden-produccion/from-order-import`, {
        items: [...payload],
        CodigoUsuarioCreacion: null,
        FechaCreacion: null,
      });
      if (res.status === 200 || res.status === 201) return res.data;
      return null;
    },
    async updateOrdenProduccionHTTP() {
      const codigo = this.ordenProduccion.Codigo;
      const { Cotizacion, ...dataToSend } = this.ordenProduccion;
      const response = await api.patch(`/orden-produccion/${codigo}`, dataToSend);
      if (response.status === 200) {
        return response;
      }
    },
    async findOrdenProduccionPorCodigoHTTP(codigo) {
      try {
        const result = await api.get(`/orden-produccion/${codigo}`);
        if (result && result.data) {
          this.ordenProduccion = result.data;
        }
      } catch (error) {
        console.error('Error en findRequestByCodigo:', error);
        this.ordenProduccion = {};
        throw error;
      }
    },
    async getDiferenciasEtHTTP(codigo) {
      const response = await api.get(`/orden-produccion/${codigo}/diferencias-et`);
      return response.data;
    },
    async actualizarEtHTTP(codigo, { actualizarTodos, camposAActualizar }) {
      const response = await api.patch(`/orden-produccion/${codigo}/actualizar-et`, {
        actualizarTodos,
        camposAActualizar,
      });
      return response.data;
    },
    async cloneOrdenProduccionHTTP(codigoOrdenProduccion, cantidad, actualizarET) {
      const authStore = useAuthStore();
      const usuario = authStore.usuario();

      const payload = {
        Cantidad: cantidad,
        CodigoUsuarioCreacion: usuario?.Codigo,
        actualizarET,
      };

      const res = await api.post(`/orden-produccion/clone/${codigoOrdenProduccion}`, payload);

      if (res.status === 200 || res.status === 201) {
        return res.data;
      }
      return null;
    },
    addEntrega() {
      this.ordenProduccion.OrdenProduccionEntregas &&
        this.ordenProduccion.OrdenProduccionEntregas.push({
          ...initial_delivery_model,
          CodigoOrdenProduccion: null,
        });
    },
    removeEntrega() {
      const deliveries = this.ordenProduccion.OrdenProduccionEntregas;
      if (deliveries && deliveries.length) {
        this.ordenProduccion.OrdenProduccionEntregas = deliveries.slice(0, deliveries.length - 1);
      }
    },
    updateEntrega({ delivery, index }) {
      if (this.ordenProduccion.OrdenProduccionEntregas && index >= 0) {
        const deliveries = [...this.ordenProduccion.OrdenProduccionEntregas];
        const { Cantidad, Fecha } = delivery;
        deliveries[index] = {
          ...this.ordenProduccion.OrdenProduccionEntregas[index],
          Cantidad,
          Fecha,
        };
        this.ordenProduccion.OrdenProduccionEntregas = deliveries;
      }
    },
    addProducto() {
      const esUnico = this.ordenProduccion.OrdenProduccionProducto.length === 0;
      const cantidadCabecera = this.ordenProduccion.OrdenProduccionCantidad?.[0]?.Cantidad;

      this.ordenProduccion.OrdenProduccionProducto.push({
        ...work_order_producto_model,
        Cantidad: esUnico ? cantidadCabecera : null,
      });
    },
    updateProducto(data) {
      this.ordenProduccion.OrdenProduccionProducto[data.index] = data.producto;
      if (this.ordenProduccion.OrdenProduccionProducto.length === 1) {
        if (
          this.ordenProduccion.OrdenProduccionCantidad &&
          this.ordenProduccion.OrdenProduccionCantidad.length > 0
        ) {
          this.ordenProduccion.OrdenProduccionCantidad[0].Cantidad = data.producto.Cantidad;
        }
      }
    },

    autoCompletarCantidadUnica() {
      const productos = this.ordenProduccion.OrdenProduccionProducto;
      const cantidadCabecera = this.ordenProduccion.OrdenProduccionCantidad?.[0]?.Cantidad;

      if (productos && productos.length === 1 && cantidadCabecera !== undefined) {
        productos[0].Cantidad = cantidadCabecera;
      }
    },
    removeProducto(index) {
      const productos = this.ordenProduccion.OrdenProduccionProducto;
      if (productos && productos.length) {
        productos.splice(index, 1);
        this.ordenProduccion.OrdenProduccionProducto = productos;
      }
    },

    resetValidaciones() {
      Object.keys(this.validarOrdenProduccionCampos).forEach((key) => {
        this.validarOrdenProduccionCampos[key] = true;
      });
      this.camposOrdenProduccionInvalidos = [];
    },
    setValidacionOrdenProduccionCantidad(value) {
      this.validarOrdenProduccionCampos.OrdenProduccionCantidad = value;
    },
    validarOrdenProduccionCantidad() {
      this.validarOrdenProduccionCampos.OrdenProduccionCantidad =
        this.ordenProduccion.OrdenProduccionCantidad.map(
          (item) => item.Cantidad !== null && item.Cantidad > 0,
        );
    },
    validarCampos() {
      const elementoBaseStore = useElementoBaseStore();
      const { elementoBaseConfig } = storeToRefs(elementoBaseStore);

      this.validarOrdenProduccionCampos = {
        AltoTotal: true,
        Base: true,
        CantidadEtiquetas: true,
        ClienteDescripcion: !!this.ordenProduccion.Cliente.Descripcion,
        CodigoCliente: !!this.ordenProduccion.CodigoCliente,
        CodigoUnidadVenta: !!this.ordenProduccion.CodigoUnidadVenta,
        EtiquetasDesarrollo: true,
        EtiquetasEje: true,
        FamiliaMaterial: true,
        Forma: true,
        GapDesarrollo: true,
        GapEje: true,
        MargenSuperior: true,
        MedidaDesarrollo: true,
        MedidaEje: true,
        Referencia: !!this.ordenProduccion.Referencia,
        Sellado: true,
        Solapa: true,
        AnchoMaterial: true,
        SolicitudCantidad: [],
      };

      const primerElemento = this.ordenProduccion.OrdenProduccionElemento[0];
      const codigoElementoBase = primerElemento?.ElementoTipo?.CodigoElementoBase;

      // COTI-507: extrusion no usa los campos del elemento base (suaje, medidas, familia
      // de material), asi que se saltean todas esas validaciones y en su lugar se validan
      // los datos del film resultante, que son los que el cotizador necesita.
      if (codigoElementoBase === ElementoBase.EXTRUSION) {
        this.validarOrdenProduccionCampos.FamiliaMaterial = true;
        this.validarCamposExtrusion(primerElemento);
      } else {
        if (elementoBaseConfig.value.campos.mostrarMedidaEje) {
          this.validarOrdenProduccionCampos.MedidaEje = !!primerElemento.MedidaEje;
        }
        if (elementoBaseConfig.value.campos.mostrarMedidaDesarrollo) {
          this.validarOrdenProduccionCampos.MedidaDesarrollo = !!primerElemento.MedidaDesarrollo;
        }
        if (elementoBaseConfig.value.campos.mostrarAnchoMaterial) {
          this.validarOrdenProduccionCampos.AnchoMaterial = !!primerElemento.AnchoMaterial;
        } else {
          this.validarOrdenProduccionCampos.AnchoMaterial = true;
        }
        // if(elementoBaseConfig.value.campos.mostrarGapEje) {
        //   this.validarOrdenProduccionCampos.GapEje = !!primerElemento.GapEje
        // }
        // if(elementoBaseConfig.value.campos.mostrarGapDesarrollo) {
        //   this.validarOrdenProduccionCampos.GapDesarrollo = !!primerElemento.GapDesarrollo
        // }
        if (elementoBaseConfig.value.campos.mostrarEtiquetasAncho) {
          this.validarOrdenProduccionCampos.EtiquetasEje = !!primerElemento.EtiquetasEje;
        }
        if (elementoBaseConfig.value.campos.mostrarEtiquetasDesarrollo) {
          this.validarOrdenProduccionCampos.EtiquetasDesarrollo =
            !!primerElemento.EtiquetasDesarrollo;
        }

        if (
          ![
            ElementoBase.TERMO_ENCOGIBLE,
            ElementoBase.CAJA,
            ElementoBase.CONSUMIBLE,
            ElementoBase.FLEXIBLE,
            ElementoBase.BOLSAS_FUELLE,
            ElementoBase.BOLSAS_BOX,
            // COTI-502: en offset el campo de familia de material no se muestra, así que
            // exigirlo dejaba el formulario inválido sin nada que el usuario pudiera corregir.
            ElementoBase.OFFSET,
          ].includes(codigoElementoBase)
        ) {
          this.validarOrdenProduccionCampos.FamiliaMaterial =
            !!primerElemento.MateriaPrimaFamilia?.Codigo;
        } else if ([ElementoBase.BOLSAS_BOX].includes(codigoElementoBase)) {
          this.validarOrdenProduccionCampos.Solapa = !!primerElemento.Solapa;
          this.validarOrdenProduccionCampos.Sellado = !!primerElemento.Sellado;
          this.validarOrdenProduccionCampos.Base = !!primerElemento.Base;
        } else if ([ElementoBase.BOLSAS_FUELLE].includes(codigoElementoBase)) {
          this.validarOrdenProduccionCampos.Solapa = !!primerElemento.Solapa;
          this.validarOrdenProduccionCampos.Base = !!primerElemento.Base;
        } else {
          this.validarOrdenProduccionCampos.FamiliaMaterial = true;
        }
      }

      this.validarOrdenProduccionCantidad();
      this.validarOrdenProduccionCampos['OrdenProduccionCantidad'] =
        this.validarOrdenProduccionCampos.OrdenProduccionCantidad.every((v) => v);

      console.debug('Validaciones de orden de producción:', this.validarOrdenProduccionCampos);
      return this.verificarValidacionPropiedades();
    },
    /**
     * Datos del film resultante que el cotizador necesita cuando el elemento se resuelve
     * por dimensionamiento continuo. Sin el gramaje —cargado directo o derivado de
     * Densidad x Espesor, según calculo_material_extrusion— no puede derivar el largo del
     * pedido y falla con "desarrolloMm debe ser > 0".
     *
     * El ancho del elemento no se valida como campo propio: en extrusión no se carga a
     * mano, sale de la sumatoria de los anchos de las bobinas del proceso. Lo que se exige
     * son las bobinas. Mismo criterio que en cotización (prices_store).
     */
    validarCamposExtrusion(elemento) {
      const { calculoMaterialExtrusion } = useConfiguracionStore();
      const esCalculoPorGramaje = calculoMaterialExtrusion === 'gramaje';

      const procesos = elemento.OrdenProduccionElementoProceso || [];
      const procesosExtrusion = procesos.filter(
        (p) => (p.Proceso?.Codigo ?? p.CodigoProceso) === Proceso.EXTRUSION,
      );
      // Todavía no se agregó el proceso de extrusión: no hay film del que hablar.
      if (procesosExtrusion.length === 0) return;

      // Mismo criterio que esSoloExtrusion en el cotizador: si la extrusión convive con
      // otros procesos, el elemento se dimensiona por pliego físico y el gramaje del film
      // no entra en el cálculo, así que no hay por qué exigirlo.
      if (procesos.length !== procesosExtrusion.length) return;

      const cargado = (valor) =>
        valor !== null && valor !== undefined && valor !== '' && Number(valor) > 0;
      const todos = (predicado) => procesosExtrusion.every(predicado);

      this.validarOrdenProduccionCampos.MaterialResultante = todos((p) =>
        cargado(p.CodigoMaterialResultante ?? p.MaterialResultante?.Codigo),
      );
      // De acá sale el ancho del material, que el cotizador exige > 0.
      this.validarOrdenProduccionCampos.BobinasExtrusion = todos((p) =>
        (p.ElementoProcesoBobinas || []).some((bobina) => cargado(bobina?.Ancho)),
      );

      if (esCalculoPorGramaje) {
        this.validarOrdenProduccionCampos.GramajeResultante = todos((p) =>
          cargado(p.GramajeResultante),
        );
      } else {
        this.validarOrdenProduccionCampos.EspesorResultante = todos((p) =>
          cargado(p.EspesorResultante),
        );
        this.validarOrdenProduccionCampos.DensidadResultante = todos((p) =>
          cargado(p.DensidadResultante),
        );
      }
    },
    verificarValidacionPropiedades() {
      this.camposOrdenProduccionInvalidos = Object.keys(this.validarOrdenProduccionCampos).filter(
        (property) => !this.validarOrdenProduccionCampos[property],
      );

      if (this.camposOrdenProduccionInvalidos.length > 0) {
        console.debug(
          `Validación fallida para las propiedades: ${this.camposOrdenProduccionInvalidos.join(', ')}`,
        );
        return false;
      }
      return true;
    },
    setPage(page) {
      this.page = page;
    },
    emptyList() {
      this.ordenesProduccion = [];
    },
    setFilters(newFilters) {
      this.filters = { ...newFilters };
    },
    resetFilter() {
      this.filters = { ...work_order_filters_model };
    },
    resetOrdenProduccion() {
      this.ordenProduccion = JSON.parse(JSON.stringify(work_order_model));
    },
    async setDefaults(codigo) {
      const elementosDefaults = await elemento_grupo_service.getGrupoDefaults(
        codigo,
        Modules.ORDEN_PRODUCCION,
      );
      console.log('elementosDefaults', elementosDefaults);
      elementosDefaults.forEach((elementoDefault, index) => {
        const elementoGenerated = generarElemento(Modules.ORDEN_PRODUCCION, elementoDefault, index);
        this.ordenProduccion.OrdenProduccionElemento[index].OrdenProduccionElementoProceso =
          elementoGenerated.OrdenProduccionElementoProceso;
      });
    },
  },
});
